import { call, cancelled, put, select } from "redux-saga/effects"
import URI from "urijs"

import api from "apis"
import {
  submitCheckoutAppliedGiftCardEvent,
  submitCheckoutAppliedPointsEvent,
} from "helpers/cart"

import {
  APPLY_GIFT_CARD,
  CHECK_GIFT_CARD,
  REMOVE_GIFT_CARD,
  setPaymentMethod,
  updateKlarnaSession,
  updateOrderData,
  validatePanels,
  setCheckoutReady,
  loadDeliveryMethods,
} from "../actions"

const apiEndpoints = {
  address: "addresses",
  all: "update_checkout",
  email: "email",
  gift_card: "gift_cards",
  reward: "reward_points",
  shipping_method: "shipping_method",
}

const actionTypeToApiEndpoint = {
  [APPLY_GIFT_CARD]: apiEndpoints["gift_card"],
  [CHECK_GIFT_CARD]: apiEndpoints["gift_card"],
  [REMOVE_GIFT_CARD]: apiEndpoints["gift_card"],
}

/**
 * Get target API url
 *
 * @param {String} endpoint
 * @return {String} secure api url
 */
export const secureApi = (endpoint) => {
  return `/secure/api/v10/checkout/${endpoint}`
}

/**
 * Get target API endpoint
 *
 * @note action type precedes param
 * @param {Object} action Update checkout action object
 * @return {String} api endpoint
 */
const getApiEndpoint = (action) => {
  const apiEndpointByActionType = actionTypeToApiEndpoint[action.type]
  const apiEndpointByParam = apiEndpoints[action.endpoint || "all"]

  return apiEndpointByActionType || apiEndpointByParam
}

/**
 * Parse API response
 *
 * @param {Object} response
 * @param {String} endpointKey
 * @return {Object} parsed response
 */
const parseResponse = (response, endpointKey) => {
  if (response["errors"] || response["address_error"]) {
    const error = response["errors"]
      ? response["errors"][0]
      : response["address_error"]
    return { error: error, nothing: true, success: false }
  }

  if (!response["data"]) return { nothing: true, success: true }

  if (endpointKey === "all") {
    const data = response["data"]
    data["order"] = data["react"]

    return { data: data, nothing: false, success: true }
  }

  return { data: response["data"], nothing: false, success: true }
}

export default function* updateCheckout(action) {
  if (action.endpoint === "none") {
    // Check if Apple/Google Pay Interface waiting for update
    if (window && window.jewlrAppleGooglePay) {
      window.jewlrAppleGooglePay.CallbackController()
    }
    return
  }

  // Determine API endpoint
  const apiEndpoint = getApiEndpoint(action)
  const endpointKey = Object.keys(apiEndpoints).find(
    (key) => apiEndpoints[key] === apiEndpoint
  )

  if (action.endpoint === "address") {
    yield put(loadDeliveryMethods(action.setFieldValue))
  }

  // Prepare API request
  const state = yield select()
  const url = secureApi(apiEndpoint)
  const query = URI.parseQuery(state.router.location.search)

  // Determine gift card action if needed
  let gift_card_action = null
  if (state.checkout.form.applyGiftCard) {
    gift_card_action = "apply"
  } else if (state.checkout.form.removeGiftCard) {
    gift_card_action = "remove"
  }

  try {
    let result = {}

    try {
      result = yield call(
        api.put,
        url,
        Object.assign(
          {},
          state.checkout.form.hydrated
            ? state.checkout.form.values
            : state.checkout.form.initialValues,
          {
            amazon_session_id: query.amazonCheckoutSessionId,
            apply_gift_card: state.checkout.form.applyGiftCard,
            check_gift_card: state.checkout.form.checkGiftCard,
            gift_card_action,
            remove_gift_card: state.checkout.form.removeGiftCard,
            remove_gift_card_id: state.checkout.form.removeGiftCardId,
            user_logged_out: state.checkout.order.order.user == null,
          }
        )
      )
    } catch (error) {
      result = error
    }

    result = parseResponse(result, endpointKey)

    if (state.checkout.form.paymentMethod == "klarna") {
      yield put(updateKlarnaSession())
    }

    // Redirection requested
    if (result && result.redirect) {
      window.location.href = result.redirect
      return
    }

    // Order data to be updated to
    let order_data = {
      coupon_message: null,
      error: null, // error toast message
      gift_card_message: null,
      gift_card_success: false,
      react: state.checkout.order.order,
      shipping_updated: false,
      show_facebook_messenger: false,
    }

    /* Handle error */
    if (
      result &&
      (result.error || !result.success || result.nothing) &&
      action.setFieldValue
    ) {
      switch (endpointKey) {
        case "gift_card":
          // Handle gift card error
          order_data.gift_card_message = result.error
          break
        case "reward":
          // Handle reward apply error
          action.setFieldValue("reward_amount", "")
          break
        case "address":
          // Handle address error
          if (result.error.includes("Bad Request: to_zip")) {
            action.setFieldValue("zip_mismatch_error", true)
          }
          break
        case "email":
          // Handle account creation error
          action.setFieldValue("account_create_error", result.error)
          break
      }
      // Update order data
      yield put(updateOrderData(order_data, action.setFieldValue))
      yield put(setCheckoutReady(true))

      if (action.callback) {
        action.callback(false)
      }

      return
    }

    const order_balance = result.data.order_balance || result.data.order.balance

    /* Handle success */
    if (endpointKey == "all" && result.reward_error && action.setFieldValue) {
      // Handle update all error
      action.setFieldValue("reward_amount", "")
    } else {
      // Handle order balance update
      if (action.setFieldValue && order_balance) {
        action.setFieldValue("account_create_error", "")
        action.setFieldValue("zip_mismatch_error", false)
        action.setFieldValue("order_balance", order_balance)
      }

      // Handle gift card apply
      if (endpointKey == "gift_card") {
        if (gift_card_action === "apply") {
          submitCheckoutAppliedGiftCardEvent()
        }

        order_data.gift_card_success = true

        if (action.setFieldValue) {
          action.setFieldValue("gift_code", "")
        }

        if (state.checkout.form.applyGiftCard) {
          if (
            result.data.order.giftcards.length >
              state.checkout.order.order.giftcards.length &&
            action.ref
          ) {
            action.ref.blur()
          } else if (
            result.data.order.giftcards.length ==
              state.checkout.order.order.giftcards.length &&
            action.ref
          ) {
            action.ref.focus()
          }
        }
      } else if (
        endpointKey === "reward" &&
        result.data.order.reward_adjustment
      ) {
        submitCheckoutAppliedPointsEvent()
      }

      order_data.react = Object.assign(order_data.react, result.data.order)
      order_data.shipping_updated = !!result.data.shipping_updated
    }

    // Update order data
    yield put(updateOrderData(order_data, action.setFieldValue))

    if (endpointKey === "gift_card") {
      if (order_balance === "0.0") {
        yield put(setPaymentMethod("jewlr"))
      } else if (state.checkout.order.selectedPaymentMethod === "jewlr") {
        yield put(setPaymentMethod(null))
      }
      yield put(validatePanels(result.data))
    }
  } catch (e) {
    console.error("Error Updating Checkout", e)
  } finally {
    if (action.callback) {
      action.callback(true)
    }

    yield put(setCheckoutReady(true))
    if (yield cancelled()) {
      console.log("Update Checkout Cancelled")
    }
  }

  // console.log("LOAD DELIVERY METHODS", state, result)
}
